/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - RecurringTab Component v2
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file RecurringTab.tsx
 * @description Tab para gestionar campañas recurrentes con modales Bootstrap
 * @version 2.0.0
 * @date 2025-11-27
 * 
 * MEJORAS v2:
 * - Modales Bootstrap en lugar de alerts JS
 * - Funcionalidad de edición completa
 * - Auto-refresh después de crear
 * - Indicador de sincronización mejorado
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSync,
  faPlay,
  faPause,
  faEdit,
  faTrash,
  faEye,
  faCalendarAlt,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faCircle,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faInstagram, faFacebookF, faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useRecurringCampaigns } from '../../../hooks/useRecurringCampaigns';
import type { RecurringCampaign, CampaignStatus } from '../../../types/scheduler.types';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface RecurringTabProps {
  onCreateCampaign?: () => void;
  onEditCampaign?: (campaign: RecurringCampaign) => void;
  onCampaignCreated?: () => void; // ← NUEVO: callback cuando se crea una campaña
}

type ConfirmAction = 'pause' | 'resume' | 'delete' | null;

interface ConfirmModalState {
  show: boolean;
  action: ConfirmAction;
  campaign: RecurringCampaign | null;
  title: string;
  message: string;
  variant: 'warning' | 'danger';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const RecurringTab: React.FC<RecurringTabProps> = ({
  onCreateCampaign,
  onEditCampaign,
  onCampaignCreated
}) => {
  // ═════════════════════════════════════════════════════════════════════════
  // HOOKS
  // ═════════════════════════════════════════════════════════════════════════
  
  const {
    campaigns,
    activeCampaigns,
    pausedCampaigns,
    completedCampaigns,
    isLoading,
    isSyncing,
    lastSync,
    error,
    updateCampaign,
    pauseCampaign,
    resumeCampaign,
    deleteCampaign,
    refresh
  } = useRecurringCampaigns();

  // ═════════════════════════════════════════════════════════════════════════
  // STATE
  // ═════════════════════════════════════════════════════════════════════════
  
  const [selectedStatus, setSelectedStatus] = useState<CampaignStatus | 'all'>('all');
  const [selectedCampaign, setSelectedCampaign] = useState<RecurringCampaign | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    message: ''
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    show: false,
    action: null,
    campaign: null,
    title: '',
    message: '',
    variant: 'warning'
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // ═════════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═════════════════════════════════════════════════════════════════════════
  
  // Refresh cuando se crea una campaña desde el padre
  React.useEffect(() => {
    if (onCampaignCreated) {
      // Este efecto se ejecutará cuando el padre notifique
      refresh();
    }
  }, [onCampaignCreated, refresh]);

  // ═════════════════════════════════════════════════════════════════════════
  // COMPUTED
  // ═════════════════════════════════════════════════════════════════════════
  
  const filteredCampaigns = selectedStatus === 'all' 
    ? campaigns 
    : campaigns.filter(c => c.status === selectedStatus);

  // Aplicar búsqueda
  const searchedCampaigns = filteredCampaigns.filter(campaign => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      campaign.name.toLowerCase().includes(search) ||
      campaign.description?.toLowerCase().includes(search) ||
      campaign.channel.toLowerCase().includes(search) ||
      campaign.message.text.toLowerCase().includes(search)
    );
  });

  // Paginación
  const totalPages = Math.ceil(searchedCampaigns.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCampaigns = searchedCampaigns.slice(startIndex, endIndex);

  // Reset page cuando cambia el filtro o búsqueda
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, searchTerm]);

  // ═════════════════════════════════════════════════════════════════════════
  // CONFIRM MODAL HELPERS
  // ═════════════════════════════════════════════════════════════════════════
  
  const showConfirm = (
    action: ConfirmAction,
    campaign: RecurringCampaign,
    title: string,
    message: string,
    variant: 'warning' | 'danger' = 'warning'
  ) => {
    setConfirmModal({
      show: true,
      action,
      campaign,
      title,
      message,
      variant
    });
  };

  const hideConfirm = () => {
    setConfirmModal({
      show: false,
      action: null,
      campaign: null,
      title: '',
      message: '',
      variant: 'warning'
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmModal.campaign || !confirmModal.action) return;

    setIsProcessing(true);
    
    try {
      switch (confirmModal.action) {
        case 'pause':
          await pauseCampaign(confirmModal.campaign.id);
          break;
        case 'resume':
          await resumeCampaign(confirmModal.campaign.id);
          break;
        case 'delete':
          await deleteCampaign(confirmModal.campaign.id);
          break;
      }
      
      hideConfirm();
    } catch (error: any) {
      console.error('Error executing action:', error);
      // El error ya se maneja en el hook
    } finally {
      setIsProcessing(false);
    }
  };

  // ═════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═════════════════════════════════════════════════════════════════════════
  
  const handlePause = (campaign: RecurringCampaign) => {
    showConfirm(
      'pause',
      campaign,
      'Pausar Campaña',
      `¿Estás seguro de pausar la campaña "${campaign.name}"? La campaña dejará de ejecutarse hasta que la reanudes.`,
      'warning'
    );
  };

  const handleResume = (campaign: RecurringCampaign) => {
    showConfirm(
      'resume',
      campaign,
      'Reanudar Campaña',
      `¿Reanudar la campaña "${campaign.name}"? La campaña volverá a ejecutarse según su programación.`,
      'warning'
    );
  };

  const handleDelete = (campaign: RecurringCampaign) => {
    showConfirm(
      'delete',
      campaign,
      'Eliminar Campaña',
      `¿ELIMINAR definitivamente la campaña "${campaign.name}"? Esta acción no se puede deshacer.`,
      'danger'
    );
  };

  const handleViewDetail = (campaign: RecurringCampaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  };

  const handleEdit = (campaign: RecurringCampaign) => {
    setSelectedCampaign(campaign);
    setEditForm({
      name: campaign.name,
      description: campaign.description || '',
      message: campaign.message.text
    });
    setEditError(null);
    setShowEditModal(true);
    
    // Notificar al padre si existe el callback (por compatibilidad)
    if (onEditCampaign) {
      onEditCampaign(campaign);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedCampaign) return;

    // Limpiar errores previos
    setEditError(null);

    // Validaciones
    if (!editForm.name.trim()) {
      setEditError('El nombre es requerido');
      return;
    }

    if (!editForm.message.trim()) {
      setEditError('El mensaje es requerido');
      return;
    }

    setIsProcessing(true);

    try {
      await updateCampaign(selectedCampaign.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        message: {
          ...selectedCampaign.message,
          text: editForm.message
        }
      });

      setShowEditModal(false);
      setSelectedCampaign(null);
      setEditError(null);
    } catch (error: any) {
      console.error('Error updating campaign:', error);
      setEditError(error.message || 'Error al actualizar la campaña');
    } finally {
      setIsProcessing(false);
    }
  };

  // ═════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═════════════════════════════════════════════════════════════════════════
  
  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp': return faWhatsapp;
      case 'instagram': return faInstagram;
      case 'facebook': return faFacebookF;
      case 'telegram': return faTelegram;
      default: return faWhatsapp;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp': return '#34E67E';
      case 'instagram': return '#E4405F';
      case 'facebook': return '#1877F2';
      case 'telegram': return '#0088CC';
      default: return '#6c757d';
    }
  };

  const getStatusBadge = (status: CampaignStatus) => {
    switch (status) {
      case 'active':
        return <span className="badge bg-success"><FontAwesomeIcon icon={faCheckCircle} /> Activa</span>;
      case 'paused':
        return <span className="badge bg-warning text-dark"><FontAwesomeIcon icon={faPause} /> Pausada</span>;
      case 'completed':
        return <span className="badge bg-secondary"><FontAwesomeIcon icon={faCheckCircle} /> Completada</span>;
      case 'error':
        return <span className="badge bg-danger"><FontAwesomeIcon icon={faTimesCircle} /> Error</span>;
    }
  };

  const getRecurrenceText = (campaign: RecurringCampaign) => {
    const recurrence = campaign.schedule?.recurrence;
    if (!recurrence) return 'No definida';
    
    switch (recurrence.pattern) {
      case 'daily':
        return `Diaria${recurrence.interval > 1 ? ` (cada ${recurrence.interval} días)` : ''}`;
      case 'weekly':
        const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
        const dayNames = recurrence.daysOfWeek?.map(d => days[d]).join(', ') || 'No definido';
        return `Semanal (${dayNames})`;
      case 'monthly':
        return `Mensual (día ${recurrence.dayOfMonth || 1})`;
      case 'custom':
        return 'Personalizada';
      case 'none':
        return 'Sin recurrencia';
      default:
        return recurrence.pattern;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSyncTime = (date: Date | null) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diff < 60) return 'hace unos segundos';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  };

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER: LOADING
  // ═════════════════════════════════════════════════════════════════════════
  
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Cargando campañas recurrentes...</p>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER: ERROR
  // ═════════════════════════════════════════════════════════════════════════
  
  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER: MAIN
  // ═════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="recurring-tab">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Campañas Recurrentes</h4>
          <p className="text-muted mb-0">
            {campaigns.length} campaña{campaigns.length !== 1 ? 's' : ''} configurada{campaigns.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {/* Indicador de sincronización */}
          <div 
            className="d-flex align-items-center gap-2 px-3 py-2 rounded"
            style={{ 
              backgroundColor: isSyncing ? '#fff3cd' : '#d1e7dd',
              color: isSyncing ? '#856404' : '#0f5132',
              border: `1px solid ${isSyncing ? '#ffc107' : '#198754'}`,
              cursor: isSyncing ? 'default' : 'pointer',
              transition: 'all 0.3s ease',
              opacity: isSyncing ? 0.8 : 1
            }}
            onClick={isSyncing ? undefined : refresh}
            title={isSyncing ? 'Sincronizando en segundo plano...' : `Actualizado ${formatSyncTime(lastSync)} - Click para sincronizar`}
          >
            <FontAwesomeIcon 
              icon={isSyncing ? faSync : faCircle} 
              spin={isSyncing}
              style={{ fontSize: '10px' }}
            />
            <span style={{ fontSize: '13px', fontWeight: 500 }}>
              {isSyncing ? 'Sincronizando' : 'Sincronizado'}
            </span>
            {!isSyncing && lastSync && (
              <span style={{ fontSize: '11px', opacity: 0.8 }}>
                • {formatSyncTime(lastSync)}
              </span>
            )}
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={onCreateCampaign}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nueva Campaña
          </button>
        </div>
      </div>

      {/* STATS CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div 
            className={`card cursor-pointer ${selectedStatus === 'all' ? 'border-primary' : ''}`}
            onClick={() => setSelectedStatus('all')}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total</h6>
                  <h3 className="mb-0">{campaigns.length}</h3>
                </div>
                <div className="text-primary fs-2">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className={`card cursor-pointer ${selectedStatus === 'active' ? 'border-success' : ''}`}
            onClick={() => setSelectedStatus('active')}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Activas</h6>
                  <h3 className="mb-0">{activeCampaigns.length}</h3>
                </div>
                <div className="text-success fs-2">
                  <FontAwesomeIcon icon={faPlay} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className={`card cursor-pointer ${selectedStatus === 'paused' ? 'border-warning' : ''}`}
            onClick={() => setSelectedStatus('paused')}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Pausadas</h6>
                  <h3 className="mb-0">{pausedCampaigns.length}</h3>
                </div>
                <div className="text-warning fs-2">
                  <FontAwesomeIcon icon={faPause} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className={`card cursor-pointer ${selectedStatus === 'completed' ? 'border-secondary' : ''}`}
            onClick={() => setSelectedStatus('completed')}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Completadas</h6>
                  <h3 className="mb-0">{completedCampaigns.length}</h3>
                </div>
                <div className="text-secondary fs-2">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text bg-white" style={{ border: '1px solid #d1d5db', borderRight: 'none' }}>
              <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '14px' }} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre, descripción, canal o mensaje..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: '1px solid #d1d5db',
                borderLeft: 'none',
                fontSize: '14px',
                padding: '8px 12px'
              }}
            />
            {searchTerm && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSearchTerm('')}
                style={{ borderRadius: '0 6px 6px 0' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="col-md-6 text-end">
          <span className="text-muted" style={{ fontSize: '13px' }}>
            Mostrando {paginatedCampaigns.length} de {searchedCampaigns.length} campañas
          </span>
        </div>
      </div>

      {/* CAMPAIGNS TABLE */}
      {searchedCampaigns.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <FontAwesomeIcon icon={searchTerm ? faSearch : faCalendarAlt} className="text-muted mb-3" size="3x" />
            <h5 className="text-muted">
              {searchTerm 
                ? `No se encontraron resultados para "${searchTerm}"`
                : selectedStatus === 'all' 
                  ? 'No hay campañas recurrentes configuradas'
                  : `No hay campañas ${selectedStatus === 'active' ? 'activas' : selectedStatus === 'paused' ? 'pausadas' : 'completadas'}`
              }
            </h5>
            {!searchTerm && (
              <p className="text-muted">
                Crea tu primera campaña recurrente para automatizar tus envíos
              </p>
            )}
            {selectedStatus === 'all' && !searchTerm && (
              <button 
                className="btn btn-primary mt-3"
                onClick={onCreateCampaign}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Crear Campaña
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
                <thead style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Canal</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Nombre</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Recurrencia</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }} className="text-center">Estado</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }} className="text-center">Stats</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }} className="text-center">Próxima</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }} className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCampaigns.map(campaign => (
                    <tr key={campaign.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      {/* Canal */}
                      <td style={{ padding: '12px 16px' }}>
                        <div 
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: getChannelColor(campaign.channel),
                            color: 'white'
                          }}
                        >
                          <FontAwesomeIcon icon={getChannelIcon(campaign.channel)} style={{ fontSize: '14px' }} />
                        </div>
                      </td>

                      {/* Nombre */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600 }}>{campaign.name}</div>
                        {campaign.description && (
                          <div className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
                            {campaign.description.substring(0, 50)}{campaign.description.length > 50 ? '...' : ''}
                          </div>
                        )}
                      </td>

                      {/* Recurrencia */}
                      <td style={{ padding: '12px 16px' }}>
                        <span className="badge bg-light text-dark" style={{ fontSize: '11px', fontWeight: 500 }}>
                          {getRecurrenceText(campaign)}
                        </span>
                      </td>

                      {/* Estado */}
                      <td style={{ padding: '12px 16px' }} className="text-center">
                        {getStatusBadge(campaign.status)}
                      </td>

                      {/* Stats */}
                      <td style={{ padding: '12px 16px' }} className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <span className="badge bg-success" style={{ fontSize: '11px' }}>
                            {campaign.stats?.successfulRuns || 0}
                          </span>
                          <span className="badge bg-danger" style={{ fontSize: '11px' }}>
                            {campaign.stats?.failedRuns || 0}
                          </span>
                        </div>
                      </td>

                      {/* Próxima ejecución */}
                      <td style={{ padding: '12px 16px', fontSize: '11px' }} className="text-center text-muted">
                        {campaign.stats?.nextRun ? formatDate(campaign.stats.nextRun).split(' ').slice(0, 2).join(' ') : 'N/A'}
                      </td>

                      {/* Acciones */}
                      <td style={{ padding: '12px 16px' }} className="text-end">
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewDetail(campaign)}
                            title="Ver detalle"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleEdit(campaign)}
                            title="Editar"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          {campaign.status === 'active' ? (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handlePause(campaign)}
                              title="Pausar"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <FontAwesomeIcon icon={faPause} />
                            </button>
                          ) : campaign.status === 'paused' ? (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleResume(campaign)}
                              title="Reanudar"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <FontAwesomeIcon icon={faPlay} />
                            </button>
                          ) : null}
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(campaign)}
                            title="Eliminar"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: '13px' }}>Mostrar:</span>
                <select
                  className="form-select form-select-sm"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{ width: 'auto', fontSize: '13px' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted" style={{ fontSize: '13px' }}>por página</span>
              </div>

              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{ fontSize: '13px' }}
                    >
                      Anterior
                    </button>
                  </li>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Mostrar solo páginas cercanas
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(page)}
                            style={{ fontSize: '13px' }}
                          >
                            {page}
                          </button>
                        </li>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <li key={page} className="page-item disabled"><span className="page-link">...</span></li>;
                    }
                    return null;
                  })}

                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{ fontSize: '13px' }}
                    >
                      Siguiente
                    </button>
                  </li>
                </ul>
              </nav>

              <div className="text-muted" style={{ fontSize: '13px' }}>
                Página {currentPage} de {totalPages}
              </div>
            </div>
          )}
        </>
      )}

      {/* CONFIRM MODAL */}
      {confirmModal.show && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content" style={{ 
              zIndex: 10000, 
              borderRadius: '12px',
              border: '1px solid rgba(44, 167, 153, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
            }}>
              <div className="modal-header border-bottom" style={{ 
                background: confirmModal.variant === 'danger' ? '#ef4444' : '#f59e0b',
                color: 'white',
                padding: '12px 20px',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <h6 className="modal-title mb-0 d-flex align-items-center" style={{ fontWeight: 600, fontSize: '14px' }}>
                  <FontAwesomeIcon 
                    icon={confirmModal.variant === 'danger' ? faExclamationTriangle : faPause} 
                    className="me-2"
                    style={{ fontSize: '13px' }}
                  />
                  {confirmModal.title}
                </h6>
                <button 
                  type="button" 
                  className="btn-close btn-close-white"
                  onClick={hideConfirm}
                  disabled={isProcessing}
                  style={{ fontSize: '12px', opacity: 0.8 }}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '16px 20px' }}>
                <p className="mb-0" style={{ fontSize: '13px', lineHeight: '1.5' }}>{confirmModal.message}</p>
              </div>
              <div className="modal-footer border-top" style={{ padding: '12px 20px', gap: '8px' }}>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  onClick={hideConfirm}
                  disabled={isProcessing}
                  style={{
                    borderRadius: '6px',
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151'
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  onClick={handleConfirmAction}
                  disabled={isProcessing}
                  style={{
                    borderRadius: '6px',
                    padding: '6px 20px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: 'none',
                    backgroundColor: confirmModal.variant === 'danger' ? '#ef4444' : '#f59e0b',
                    color: 'white'
                  }}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" style={{ width: '12px', height: '12px' }}></span>
                      Procesando...
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedCampaign && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '550px' }}>
            <div className="modal-content" style={{ 
              zIndex: 10000,
              borderRadius: '12px',
              border: '1px solid rgba(44, 167, 153, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
            }}>
              <div className="modal-header border-bottom" style={{ 
                background: '#34E67E',
                color: 'white',
                padding: '12px 20px',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <h6 className="modal-title mb-0 d-flex align-items-center" style={{ fontWeight: 600, fontSize: '14px' }}>
                  <FontAwesomeIcon icon={faEye} className="me-2" style={{ fontSize: '13px' }} />
                  {selectedCampaign.name}
                </h6>
                <button 
                  type="button" 
                  className="btn-close btn-close-white"
                  onClick={() => setShowDetailModal(false)}
                  style={{ fontSize: '12px', opacity: 0.8 }}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '16px 20px', maxHeight: '65vh', overflowY: 'auto' }}>
                {selectedCampaign.description && (
                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '4px' }}>DESCRIPCIÓN</label>
                    <p style={{ fontSize: '13px', marginBottom: 0 }}>{selectedCampaign.description}</p>
                  </div>
                )}
                
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '4px' }}>CANAL</label>
                    <div style={{ fontSize: '13px', textTransform: 'capitalize' }}>{selectedCampaign.channel}</div>
                  </div>
                  <div className="col-6">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '4px' }}>RECURRENCIA</label>
                    <div style={{ fontSize: '13px' }}>{getRecurrenceText(selectedCampaign)}</div>
                  </div>
                </div>

                <div className="mb-3">
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '4px' }}>MENSAJE</label>
                  <pre className="bg-light p-2 rounded mb-0" style={{ 
                    whiteSpace: 'pre-wrap', 
                    fontSize: '12px',
                    fontFamily: 'Consolas, Monaco, monospace',
                    border: '1px solid #e5e7eb',
                    maxHeight: '120px',
                    overflowY: 'auto'
                  }}>
                    {selectedCampaign.message.text}
                  </pre>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '8px' }}>ESTADÍSTICAS</label>
                  <div className="row g-2" style={{ fontSize: '12px' }}>
                    <div className="col-4 text-center p-2" style={{ backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                      <div className="text-muted" style={{ fontSize: '11px' }}>Total</div>
                      <div style={{ fontWeight: 600, fontSize: '16px' }}>{selectedCampaign.stats?.totalRuns || 0}</div>
                    </div>
                    <div className="col-4 text-center p-2" style={{ backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                      <div className="text-muted" style={{ fontSize: '11px' }}>Exitosas</div>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: '#10b981' }}>{selectedCampaign.stats?.successfulRuns || 0}</div>
                    </div>
                    <div className="col-4 text-center p-2" style={{ backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                      <div className="text-muted" style={{ fontSize: '11px' }}>Fallidas</div>
                      <div style={{ fontWeight: 600, fontSize: '16px', color: '#ef4444' }}>{selectedCampaign.stats?.failedRuns || 0}</div>
                    </div>
                  </div>
                  <div className="mt-2 p-2 text-center" style={{ backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '11px' }}>
                    <span className="text-muted">Próxima:</span> <strong>{formatDate(selectedCampaign.stats?.nextRun)}</strong>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top" style={{ padding: '12px 20px', gap: '8px' }}>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  onClick={() => setShowDetailModal(false)}
                  style={{
                    borderRadius: '6px',
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151'
                  }}
                >
                  Cerrar
                </button>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleEdit(selectedCampaign);
                  }}
                  style={{
                    borderRadius: '6px',
                    padding: '6px 20px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: 'none',
                    backgroundColor: '#34E67E',
                    color: 'white'
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-1" style={{ fontSize: '12px' }} />
                  Editar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && selectedCampaign && (
        <>
          <div 
            className="modal show d-block" 
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 9999 
            }} 
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px' }}>
              <div 
                className="modal-content" 
                style={{ 
                  zIndex: 10000,
                  borderRadius: '12px',
                  border: '1px solid rgba(44, 167, 153, 0.2)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
                }}
              >
                {/* Header compacto */}
                <div 
                  className="modal-header border-bottom" 
                  style={{ 
                    background: '#34E67E',
                    color: 'white',
                    padding: '12px 20px',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <h6 className="modal-title mb-0 d-flex align-items-center" style={{ fontWeight: 600, fontSize: '15px' }}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" style={{ fontSize: '14px' }} />
                    Editar Campaña
                  </h6>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditError(null);
                    }}
                    disabled={isProcessing}
                    style={{ fontSize: '12px', opacity: 0.8 }}
                  ></button>
                </div>

                <div className="modal-body" style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
                  {/* Alert de error compacto */}
                  {editError && (
                    <div 
                      className="alert alert-danger alert-dismissible fade show mb-3 py-2" 
                      role="alert"
                      style={{ fontSize: '13px', borderRadius: '8px' }}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" style={{ fontSize: '12px' }} />
                      {editError}
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setEditError(null)}
                        style={{ fontSize: '10px', padding: '8px' }}
                      ></button>
                    </div>
                  )}
                  
                  {/* Nombre */}
                  <div className="mb-3">
                    <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                      Nombre <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      disabled={isProcessing}
                      placeholder="Nombre de la campaña"
                      style={{
                        borderRadius: '6px',
                        fontSize: '14px',
                        padding: '8px 12px'
                      }}
                    />
                  </div>

                  {/* Descripción */}
                  <div className="mb-3">
                    <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                      Descripción
                    </label>
                    <textarea 
                      className="form-control form-control-sm" 
                      rows={2} 
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      disabled={isProcessing}
                      placeholder="Descripción opcional"
                      style={{
                        borderRadius: '6px',
                        fontSize: '13px',
                        padding: '8px 12px',
                        resize: 'none'
                      }}
                    />
                  </div>

                  {/* Mensaje */}
                  <div className="mb-3">
                    <label className="form-label mb-1" style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                      Mensaje <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea 
                      className="form-control form-control-sm" 
                      rows={4} 
                      value={editForm.message}
                      onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                      disabled={isProcessing}
                      placeholder="Texto del mensaje"
                      style={{
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontFamily: 'Consolas, Monaco, monospace',
                        padding: '8px 12px',
                        resize: 'vertical',
                        minHeight: '80px'
                      }}
                    />
                    <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                      Variables: <code style={{ fontSize: '11px' }}>{'{{nombre}}'}</code>, <code style={{ fontSize: '11px' }}>{'{{email}}'}</code>
                    </small>
                  </div>

                  {/* Info compacta */}
                  <div className="d-flex gap-2 text-muted" style={{ fontSize: '12px', padding: '8px 0' }}>
                    <span><strong>{selectedCampaign.stats?.totalRuns || 0}</strong> ejecuciones</span>
                    <span>•</span>
                    <span className="text-success"><strong>{selectedCampaign.stats?.successfulRuns || 0}</strong> ok</span>
                    <span>•</span>
                    <span className="text-danger"><strong>{selectedCampaign.stats?.failedRuns || 0}</strong> error</span>
                  </div>
                </div>

                {/* Footer compacto */}
                <div 
                  className="modal-footer border-top" 
                  style={{ 
                    padding: '12px 20px',
                    gap: '8px',
                    borderTop: '1px solid #e5e7eb'
                  }}
                >
                  <button 
                    type="button" 
                    className="btn btn-sm"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditError(null);
                    }}
                    disabled={isProcessing}
                    style={{
                      borderRadius: '6px',
                      padding: '6px 16px',
                      fontSize: '13px',
                      fontWeight: 500,
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: '#374151'
                    }}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-sm"
                    onClick={handleSaveEdit}
                    disabled={isProcessing || !editForm.name.trim() || !editForm.message.trim()}
                    style={{
                      borderRadius: '6px',
                      padding: '6px 20px',
                      fontSize: '13px',
                      fontWeight: 600,
                      border: 'none',
                      backgroundColor: '#34E67E',
                      color: 'white',
                      opacity: (isProcessing || !editForm.name.trim() || !editForm.message.trim()) ? 0.5 : 1
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" style={{ width: '12px', height: '12px' }}></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" style={{ fontSize: '12px' }} />
                        Guardar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecurringTab;